import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiClock, FiCalendar, FiShare2, FiLinkedin, FiTwitter, FiLink, FiCheck } from 'react-icons/fi';
import './blog.css';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [copied, setCopied] = useState(false);

  const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/')) {
      return `${API_URL}${url}`;
    }
    return url;
  };

  // Read progress bar calculation
  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (totalScroll > 0) {
        const scrolled = (window.scrollY / totalScroll) * 100;
        setScrollProgress(scrolled);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch blog data
  useEffect(() => {
    fetchBlogAndRelated();
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const fetchBlogAndRelated = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get main blog
      const res = await fetch(`${API_URL}/api/blogs/${id}`);
      const json = await res.json();
      
      if (res.ok && json.data) {
        setBlog(json.data);
        
        // Fetch matching category related posts
        const relRes = await fetch(`${API_URL}/api/blogs?category=${encodeURIComponent(json.data.category)}`);
        const relJson = await relRes.json();
        if (relRes.ok && relJson.data) {
          // Filter out current blog and limit to 3 items
          const filtered = relJson.data.filter(b => b._id !== json.data._id && b.slug !== json.data.slug).slice(0, 3);
          setRelated(filtered);
        }
      } else {
        setError(json.message || 'Article not found');
      }
    } catch (err) {
      setError('Connection to server failed.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => {
          console.error('Failed to copy using clipboard API, trying fallback:', err);
          fallbackCopyText(url);
        });
    } else {
      fallbackCopyText(url);
    }
  };

  const fallbackCopyText = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        alert('Could not copy link.');
      }
    } catch (err) {
      console.error('Fallback copy error:', err);
    }
    document.body.removeChild(textArea);
  };

  const parseInlineMarkdown = (text) => {
    if (!text) return '';
    let processed = text;
    // Bold: **text** -> <strong>text</strong>, __text__ -> <strong>text</strong>
    processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    processed = processed.replace(/__(.*?)__/g, '<strong>$1</strong>');
    // Italic: *text* -> <em>text</em>, _text_ -> <em>text</em>
    processed = processed.replace(/\*(.*?)\*/g, '<em>$1</em>');
    processed = processed.replace(/_(.*?)_/g, '<em>$1</em>');
    // Inline code: `code` -> <code>code</code>
    processed = processed.replace(/`(.*?)`/g, '<code class="blog__content-code-inline" style="background: rgba(0,0,0,0.05); padding: 0.2rem 0.4rem; border-radius: 4px; font-family: monospace;">$1</code>');
    // Images: ![alt](url) -> img tag
    processed = processed.replace(/!\[(.*?)\]\((.*?)\)/g, (match, alt, url) => {
      const fullUrl = url.startsWith('/api/') ? `${API_URL}${url}` : url;
      return `<img src="${fullUrl}" alt="${alt}" class="blog__content-image" style="max-width: 100%; border-radius: 8px; margin: 1rem 0; display: block;" />`;
    });
    // Links: [title](url) -> anchor tag
    processed = processed.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="blog__content-link">$1</a>');
    return processed;
  };

  // Helper to parse content line breaks into standard styled paragraphs with markdown elements
  const renderContent = (content) => {
    if (!content) return null;
    
    const lines = content.split('\n');
    const renderedElements = [];
    let currentList = [];
    let listType = null; // 'bullet' or 'ordered'
    let inCodeBlock = false;
    let codeContent = [];
    
    const flushList = (key) => {
      if (currentList.length > 0) {
        if (listType === 'bullet') {
          renderedElements.push(
            <ul key={`list-${key}`} className="blog__detail-list">
              {currentList.map((item, idx) => (
                <li key={idx} className="blog__detail-list-item" dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(item) }} />
              ))}
            </ul>
          );
        } else if (listType === 'ordered') {
          renderedElements.push(
            <ol key={`list-${key}`} className="blog__detail-list-ordered" style={{ paddingLeft: '1.5rem', marginBottom: '1.5rem' }}>
              {currentList.map((item, idx) => (
                <li key={idx} className="blog__detail-list-item" style={{ marginBottom: '0.4rem' }} dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(item) }} />
              ))}
            </ol>
          );
        }
        currentList = [];
        listType = null;
      }
    };

    lines.forEach((line, i) => {
      const trimmed = line.trim();
      
      // Code Blocks
      if (trimmed.startsWith('```')) {
        if (inCodeBlock) {
          renderedElements.push(
            <pre key={`code-${i}`} className="blog__detail-code-block" style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '8px', overflowX: 'auto', fontFamily: 'monospace', margin: '1.5rem 0', border: '1px solid rgba(0,0,0,0.05)' }}>
              <code>{codeContent.join('\n')}</code>
            </pre>
          );
          codeContent = [];
          inCodeBlock = false;
        } else {
          flushList(i);
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeContent.push(line);
        return;
      }

      // Horizontal Rules (3 or more hyphens, asterisks, or underscores)
      if (/^(?:-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
        flushList(i);
        renderedElements.push(
          <hr key={i} className="blog__detail-hr" style={{ border: '0', height: '1px', background: '#e0e0e0', margin: '2rem 0', display: 'block' }} />
        );
        return;
      }

      if (!trimmed) {
        flushList(i);
        renderedElements.push(
          <div key={`br-${i}`} style={{ height: '1.2rem' }}></div>
        );
        return;
      }

      // Inline alignment wrappers (check if line starts with HTML alignment tag)
      if (trimmed.startsWith('<div style="text-align:') && trimmed.endsWith('</div>')) {
        flushList(i);
        renderedElements.push(
          <div key={i} dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(trimmed) }} />
        );
        return;
      }

      // Blockquotes
      if (trimmed.startsWith('> ')) {
        flushList(i);
        renderedElements.push(
          <blockquote key={i} className="blog__detail-blockquote" style={{ borderLeft: '4px solid var(--color-primary)', paddingLeft: '1rem', fontStyle: 'italic', margin: '1.5rem 0', color: '#555' }} dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(trimmed.replace(/^>\s*/, '')) }} />
        );
        return;
      }

      // Bullet List Items
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        if (listType !== 'bullet') {
          flushList(i);
          listType = 'bullet';
        }
        const itemText = trimmed.replace(/^[-*]\s+/, '');
        currentList.push(itemText);
        return;
      }

      // Ordered List Items
      if (/^\d+\.\s+/.test(trimmed)) {
        if (listType !== 'ordered') {
          flushList(i);
          listType = 'ordered';
        }
        const itemText = trimmed.replace(/^\d+\.\s+/, '');
        currentList.push(itemText);
        return;
      }

      // Flush list if any other element is encountered
      flushList(i);

      // Headings
      if (trimmed.startsWith('###')) {
        renderedElements.push(
          <h4 key={i} className="blog__detail-subheading-small" style={{ fontSize: '1.25rem', marginTop: '1.5rem', fontWeight: 600 }} dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(trimmed.replace(/^###\s*/, '')) }} />
        );
      } else if (trimmed.startsWith('##')) {
        renderedElements.push(
          <h3 key={i} className="blog__detail-subheading" style={{ fontSize: '1.5rem', marginTop: '1.8rem', fontWeight: 600 }} dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(trimmed.replace(/^##\s*/, '')) }} />
        );
      } else if (trimmed.startsWith('#')) {
        renderedElements.push(
          <h2 key={i} className="blog__detail-subheading-large" style={{ fontSize: '1.8rem', marginTop: '2rem', fontWeight: 700 }} dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(trimmed.replace(/^#\s*/, '')) }} />
        );
      } else {
        // Paragraph
        renderedElements.push(
          <p key={i} className="blog__detail-paragraph" dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(line) }} />
        );
      }
    });

    // Flush any remaining list at the end
    flushList(lines.length);

    return renderedElements;
  };


  if (loading) return <div className="blog__loading-screen">Loading article content...</div>;
  if (error) return (
    <div className="blog__error-screen">
      <h3>Error Loading Blog</h3>
      <p>{error}</p>
      <button className="btn primary" onClick={() => navigate('/blog')}>Back to Blogs</button>
    </div>
  );
  if (!blog) return null;

  return (
    <div className="blog__detail-page">
      {/* READING PROGRESS BAR */}
      <div 
        className="blog__progress-bar" 
        style={{ width: `${scrollProgress}%` }}
        role="progressbar"
        aria-valuenow={scrollProgress}
        aria-valuemin="0"
        aria-valuemax="100"
      ></div>

      <div className="container blog__detail-container">
        
        {/* BACK TO BLOGS BUTTON */}
        <button className="blog__back-btn" onClick={() => navigate('/blog')}>
          <FiArrowLeft /> Back to Articles
        </button>

        {/* HERO TITLE */}
        <header className="blog__detail-header">
          <span className="blog__detail-cat-tag">{blog.category}</span>
          <h1 className="blog__detail-title">{blog.title}</h1>
          {blog.subtitle && <p className="blog__detail-subtitle">{blog.subtitle}</p>}

          <div className="blog__detail-meta-row">
            <div className="blog__author-card">
              <div className="blog__author-avatar">
                {blog.authorName.charAt(0)}
              </div>
              <div className="blog__author-info">
                <span className="blog__author-name">{blog.authorName}</span>
                <span className="blog__author-role">SkillBridge Author</span>
              </div>
            </div>

            <div className="blog__meta-stats">
              <span className="blog__meta-stat"><FiCalendar /> {new Date(blog.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              <span className="blog__meta-stat"><FiClock /> {blog.readTime || '5 min read'}</span>
            </div>
          </div>
        </header>

        {/* FEATURED COVER IMAGE */}
        <div className="blog__detail-image-wrapper">
          <img 
            src={getFullImageUrl(blog.imageUrl) || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=1200&q=80'} 
            alt={blog.title} 
            className="blog__detail-cover-img"
          />
        </div>

        {/* MAIN BODY LAYOUT */}
        <div className="blog__detail-body-layout">
          
          {/* SHARE SIDEBAR */}
          <aside className="blog__share-sidebar">
            <span className="blog__share-label"><FiShare2 /> Share</span>
            <div className="blog__share-icons">
              <a 
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="blog__share-icon linkedin"
                title="Share on LinkedIn"
              >
                <FiLinkedin />
              </a>
              <a 
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(blog.title)}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="blog__share-icon twitter"
                title="Share on Twitter"
              >
                <FiTwitter />
              </a>
              <button 
                className={`blog__share-icon copy-link ${copied ? 'copied' : ''}`}
                onClick={handleCopyLink}
                title="Copy Link to Clipboard"
              >
                {copied ? <FiCheck /> : <FiLink />}
              </button>
            </div>
          </aside>

          {/* MAIN ARTICLE CONTENT */}
          <article className="blog__article-content">
            {renderContent(blog.content)}
            
            {blog.tags && blog.tags.length > 0 && (
              <div className="blog__detail-tags-section">
                {blog.tags.map((tag, idx) => (
                  <span key={idx} className="blog__detail-tag">#{tag}</span>
                ))}
              </div>
            )}
          </article>
          
        </div>

        {/* RELATED ARTICLES */}
        {related.length > 0 && (
          <section className="blog__related-section">
            <h3 className="blog__related-heading">Related Articles</h3>
            <div className="blog__related-grid">
              {related.map(item => (
                <Link 
                  key={item._id} 
                  to={`/blog/${item.slug || item._id}`}
                  className="blog__related-card"
                  style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                >
                  <img 
                    src={getFullImageUrl(item.imageUrl) || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=400&q=80'} 
                    alt={item.title} 
                    className="blog__related-img"
                  />
                  <div className="blog__related-body">
                    <span className="blog__related-cat">{item.category}</span>
                    <h4 className="blog__related-title">{item.title}</h4>
                    <span className="blog__related-read"><FiClock /> {item.readTime || '3 min read'}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

      </div>
    </div>
  );
};

export default BlogDetail;
